import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../../../services/api'
import { createDefaultIncomeReportConfig } from './incomeReportCalculations'

const serialize = (value) => JSON.stringify(value)

// Both editors share the same revision protocol. Refs keep header/interval
// callbacks current without capturing an old render's values.
export default function useIncomeReportEditor(id, { configOnly = false } = {}) {
  const [payload, setPayload] = useState(null)
  const [sourceRows, setSourceRows] = useState([])
  const [config, setConfigState] = useState(createDefaultIncomeReportConfig())
  const [overrides, setOverridesState] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState({ type: '', text: '' })
  const activeId = useRef(id)
  activeId.current = id
  const session = useRef(null)
  const requestNumber = useRef(0)

  const setConfig = (next) => {
    const current = session.current
    if (!current || current.id !== id) return
    current.config = typeof next === 'function' ? next(current.config) : next
    setConfigState(current.config)
    setMessage({ type: '', text: '' })
  }
  const setOverrides = (next) => {
    const current = session.current
    if (!current || current.id !== id) return
    current.overrides = typeof next === 'function' ? next(current.overrides) : next
    setOverridesState(current.overrides)
    setMessage({ type: '', text: '' })
  }

  const fetchReport = useCallback(async () => {
    const request = ++requestNumber.current
    setLoading(true)
    setError('')
    setMessage({ type: '', text: '' })
    try {
      const response = await api.get(`/company/reports/${id}/income`)
      if (activeId.current !== id || request !== requestNumber.current) return
      const data = response.data || {}
      const nextConfig = createDefaultIncomeReportConfig(data.config || {})
      const nextOverrides = data.overrides || []
      session.current = {
        id, revision: data.revision, canEdit: data.can_edit,
        config: nextConfig, overrides: nextOverrides,
        savedConfig: serialize(nextConfig), savedOverrides: serialize(nextOverrides),
        saving: false, conflict: false,
      }
      setPayload(data)
      setSourceRows(data.source_rows || [])
      setConfigState(nextConfig)
      setOverridesState(nextOverrides)
      setSaving(false)
    } catch (err) {
      if (activeId.current === id && request === requestNumber.current) {
        setError(err.response?.data?.error || 'Не удалось загрузить отчёт')
      }
    } finally {
      if (activeId.current === id && request === requestNumber.current) setLoading(false)
    }
  }, [id])

  useEffect(() => {
    session.current = null
    fetchReport()
    return () => { requestNumber.current += 1; session.current = null }
  }, [fetchReport])

  const hasUnsavedChanges = () => {
    const current = session.current
    return Boolean(current && current.id === id && current.canEdit && (
      serialize(current.config) !== current.savedConfig ||
      (!configOnly && serialize(current.overrides) !== current.savedOverrides)
    ))
  }

  const persistReport = async ({ silent = false } = {}) => {
    const current = session.current
    if (!current || current.id !== id || activeId.current !== id || !current.canEdit || current.saving) return false
    if (silent && current.conflict) return false
    const sentConfig = serialize(current.config)
    const sentOverrides = serialize(current.overrides)
    if (sentConfig === current.savedConfig && (configOnly || sentOverrides === current.savedOverrides)) {
      if (!silent) setMessage({ type: 'success', text: 'Несохранённых изменений нет' })
      return false
    }
    current.saving = true
    setSaving(true)
    setError('')
    setMessage({ type: '', text: '' })
    const isCurrent = () => session.current === current && activeId.current === id
    try {
      const body = configOnly
        ? { ...JSON.parse(sentConfig), expected_revision: current.revision }
        : { config: JSON.parse(sentConfig), overrides: JSON.parse(sentOverrides), expected_revision: current.revision }
      const response = await api.put(`/company/reports/${id}/income${configOnly ? '/config' : ''}`, body)
      if (!isCurrent()) return false
      const nextConfig = createDefaultIncomeReportConfig(response.data.config)
      const nextOverrides = response.data.overrides || []
      current.revision = response.data.revision
      current.conflict = false
      // A late response must not replace edits typed while the request was running.
      const configUnchanged = serialize(current.config) === sentConfig
      const overridesUnchanged = serialize(current.overrides) === sentOverrides
      current.savedConfig = configUnchanged ? serialize(nextConfig) : sentConfig
      current.savedOverrides = overridesUnchanged ? serialize(nextOverrides) : sentOverrides
      if (configUnchanged) { current.config = nextConfig; setConfigState(nextConfig) }
      if (overridesUnchanged) { current.overrides = nextOverrides; setOverridesState(nextOverrides) }
      if (!silent) setMessage({
        type: 'success',
        text: configUnchanged && overridesUnchanged ? 'Изменения сохранены' : 'Изменения сохранены. Есть новые несохранённые правки.',
      })
      return true
    } catch (err) {
      if (isCurrent()) {
        current.conflict = [409, 428].includes(err.response?.status)
        setError(err.response?.data?.error || 'Не удалось сохранить отчёт. Ваши правки остаются на экране.')
      }
      return false
    } finally {
      current.saving = false
      if (isCurrent()) setSaving(false)
    }
  }

  return {
    payload, setPayload, sourceRows, setSourceRows, config, setConfig, overrides, setOverrides,
    loading, saving, error, setError, message, setMessage, fetchReport, persistReport, hasUnsavedChanges,
  }
}
